/**
 * Preferências de câmera/microfone (localStorage) + enumeração de devices.
 */

const AUDIO_KEY = "xpro.video.preferredAudioDeviceId";
const VIDEO_KEY = "xpro.video.preferredVideoDeviceId";
const WAIT_SOUND_MUTE_KEY = "xpro.video.waitSoundMuted";
const VO_SPEAKER_MUTE_KEY = "xpro.virtualOffice.speakerMuted";

function safeGet(key) {
  try {
    return String(localStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function safeSet(key, value) {
  try {
    const v = String(value || "").trim();
    if (!v) localStorage.removeItem(key);
    else localStorage.setItem(key, v);
  } catch {
    /* ignore */
  }
}

export function getPreferredAudioDeviceId() {
  return safeGet(AUDIO_KEY);
}

export function getPreferredVideoDeviceId() {
  return safeGet(VIDEO_KEY);
}

export function setPreferredAudioDeviceId(deviceId) {
  safeSet(AUDIO_KEY, deviceId);
}

export function setPreferredVideoDeviceId(deviceId) {
  safeSet(VIDEO_KEY, deviceId);
}

/** Som de espera: default ligado (mute flag ausente / false). */
export function isWaitSoundMuted() {
  try {
    return localStorage.getItem(WAIT_SOUND_MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setWaitSoundMuted(muted) {
  try {
    if (muted) localStorage.setItem(WAIT_SOUND_MUTE_KEY, "1");
    else localStorage.removeItem(WAIT_SOUND_MUTE_KEY);
  } catch {
    /* ignore */
  }
}

/** Áudio dos colegas no escritório: default ligado (mute flag ausente / false). */
export function isVoSpeakerMuted() {
  try {
    return localStorage.getItem(VO_SPEAKER_MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setVoSpeakerMuted(muted) {
  try {
    if (muted) localStorage.setItem(VO_SPEAKER_MUTE_KEY, "1");
    else localStorage.removeItem(VO_SPEAKER_MUTE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @returns {Promise<{ audioInputs: MediaDeviceInfo[], videoInputs: MediaDeviceInfo[] }>}
 */
export async function enumerateVideoCallDevices() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
    return { audioInputs: [], videoInputs: [] };
  }
  try {
    const list = await navigator.mediaDevices.enumerateDevices();
    return {
      audioInputs: list.filter((d) => d.kind === "audioinput"),
      videoInputs: list.filter((d) => d.kind === "videoinput"),
    };
  } catch {
    return { audioInputs: [], videoInputs: [] };
  }
}

/**
 * Constraints para Twilio createLocalTracks / LocalTrack recreate.
 */
export function buildAudioConstraint(deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return true;
  return { deviceId: { exact: id } };
}

export function buildVideoConstraint(deviceId, { width = 1280, height = 720, frameRate = 24 } = {}) {
  const id = String(deviceId || "").trim();
  const base = { width, height, frameRate };
  if (!id) return base;
  return { ...base, deviceId: { exact: id } };
}

/**
 * Beep curto (Web Audio) para lembrete de espera — sem arquivo externo.
 * @returns {() => void} stop
 */
export function playWaitBeep({ frequency = 660, durationMs = 140, volume = 0.04 } = {}) {
  if (typeof window === "undefined") return () => {};
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return () => {};
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = frequency;
    gain.gain.value = 0;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.linearRampToValueAtTime(volume, now + 0.02);
    gain.gain.linearRampToValueAtTime(0.0001, now + durationMs / 1000);
    osc.start(now);
    osc.stop(now + durationMs / 1000 + 0.05);
    const cleanup = () => {
      try {
        osc.disconnect();
        gain.disconnect();
        ctx.close();
      } catch {
        /* ignore */
      }
    };
    setTimeout(cleanup, durationMs + 200);
    return cleanup;
  } catch {
    return () => {};
  }
}

const GENERIC_AUDIO_IDS = new Set(["default", "communications"]);

function deviceIdOf(device) {
  return String(device?.deviceId || "").trim();
}

/**
 * Meet: desconectar o fone cai no mic do sistema; plugar um headset novo
 * troca automaticamente para ele. Câmera só faz fallback se a atual sumir.
 */
export function resolveAudioDeviceAfterChange({
  previousIds = new Set(),
  devices = [],
  currentId = "",
  preferredId = "",
  currentLost = false,
} = {}) {
  const list = (Array.isArray(devices) ? devices : []).filter((d) => deviceIdOf(d));
  const ids = new Set(list.map(deviceIdOf));
  const current = String(currentId || "").trim();
  const preferred = String(preferredId || "").trim();
  const prev = previousIds instanceof Set ? previousIds : new Set(previousIds || []);
  const lost = Boolean(currentLost || (current && !ids.has(current)));

  const seenBefore = prev.size > 0;
  const added = seenBefore
    ? list.filter((d) => {
        const id = deviceIdOf(d);
        if (!id || prev.has(id) || GENERIC_AUDIO_IDS.has(id)) return false;
        return true;
      })
    : [];

  if (lost) {
    if (preferred && preferred !== current && ids.has(preferred)) return preferred;
    const systemDefault = list.find((d) => deviceIdOf(d) === "default");
    if (systemDefault) return deviceIdOf(systemDefault);
    const fallback = list.find((d) => deviceIdOf(d) !== current);
    return deviceIdOf(fallback) || deviceIdOf(list[0]) || "";
  }

  if (added.length) {
    return deviceIdOf(added[added.length - 1]);
  }

  if (current && ids.has(current)) return current;
  if (preferred && ids.has(preferred)) return preferred;
  return deviceIdOf(list[0]) || "";
}

export function resolveVideoDeviceAfterChange({
  devices = [],
  currentId = "",
  preferredId = "",
} = {}) {
  const list = (Array.isArray(devices) ? devices : []).filter((d) => deviceIdOf(d));
  const ids = new Set(list.map(deviceIdOf));
  const current = String(currentId || "").trim();
  const preferred = String(preferredId || "").trim();
  if (current && ids.has(current)) return current;
  if (preferred && ids.has(preferred)) return preferred;
  return deviceIdOf(list[0]) || "";
}

export function localTrackDeviceId(track) {
  try {
    return String(track?.mediaStreamTrack?.getSettings?.()?.deviceId || "").trim();
  } catch {
    return "";
  }
}

export function bindLocalMediaTrackEnded(track, onEnded) {
  if (!track || typeof onEnded !== "function") return () => {};
  const media = track.mediaStreamTrack;
  const handle = () => {
    try {
      onEnded(track);
    } catch {
      /* ignore */
    }
  };
  try {
    track.on?.("stopped", handle);
  } catch {
    /* ignore */
  }
  try {
    media?.addEventListener?.("ended", handle);
  } catch {
    /* ignore */
  }
  return () => {
    try {
      track.off?.("stopped", handle);
    } catch {
      /* ignore */
    }
    try {
      media?.removeEventListener?.("ended", handle);
    } catch {
      /* ignore */
    }
  };
}

/**
 * URL convidável: remove role=agent (e afins) para o link do convidado.
 */
export function buildGuestShareUrl(href = "") {
  try {
    const u = new URL(href || (typeof window !== "undefined" ? window.location.href : ""));
    u.searchParams.delete("role");
    u.searchParams.delete("name");
    u.searchParams.delete("displayName");
    return u.toString();
  } catch {
    return String(href || "");
  }
}
